import { AnimatePresence, m, type Variants } from "motion/react";
import * as React from "react";
import { cn } from "../../lib/utils";

import { SPRING_TRANSITION } from "../shared/constants";
import {
	type FloatingToolbarColors,
	getToolbarColors,
	type ToolbarVariant,
} from "./toolbar-colors";
import { ToolbarContext } from "./toolbar-context";
import type { FloatingToolbarScrollBehavior } from "./toolbar-scroll-behavior";
import { FloatingToolbarTokens } from "./toolbar-tokens";

export interface FloatingToolbarProps {
	/** Whether the toolbar is expanded, showing startContent and endContent */
	expanded: boolean;
	/** Orientation of the toolbar */
	orientation?: "horizontal" | "vertical";
	/** Color variant: standard, vibrant, surface-high, surface-highest, tertiary, or xr */
	variant?: ToolbarVariant;
	/** Color configuration override */
	colors?: FloatingToolbarColors;
	/** Shape variant: full = pill shape (default), large = large rounded */
	shape?: "full" | "large";
	/** Padding inside toolbar container */
	contentPadding?: React.CSSProperties | string;
	/** Scroll behavior hook result */
	scrollBehavior?: FloatingToolbarScrollBehavior;
	/** Start content (shown when expanded, usually on the left/top) */
	startContent?: React.ReactNode;
	/** End content (shown when expanded, usually on the right/bottom) */
	endContent?: React.ReactNode;
	/** Main toolbar content (always visible) */
	children: React.ReactNode;
	/** Whether to disable the internal scroll translation (useful when parent handles it) */
	disableScrollTranslation?: boolean;
	/** Whether to disable layout animation (useful when nested inside FloatingToolbarWithFab) */
	disableLayoutAnimation?: boolean;
	/**
	 * Gap (px) between items in the center children slot.
	 * Corresponds to MD3 `ContainerBetweenSpace` token (4dp default).
	 * @default 4
	 */
	itemGap?: number;
	/** Alignment (justify-content) of the center children slot */
	childrenAlignment?: "start" | "center" | "end";
	/** Custom CSS class applied to each child item in the toolbar slots */
	itemClassName?: string;
	className?: string;
	style?: React.CSSProperties;
	"aria-label"?: string;
}

// --- Module-level helpers ---

const ALIGNMENT_CLASS: Record<"start" | "center" | "end", string> = {
	start: "justify-start",
	center: "justify-center",
	end: "justify-end",
};

function resolveScrollTranslation(
	scrollBehavior: FloatingToolbarScrollBehavior | undefined,
	disabled: boolean | undefined,
): { x: number | string; y: number | string } {
	if (!scrollBehavior || disabled) return { x: 0, y: 0 };
	const { offset, exitDirection } = scrollBehavior;
	const percent = offset * -100;
	switch (exitDirection) {
		case "top":
			return { y: `${-percent}%`, x: 0 };
		case "bottom":
			return { y: `${percent}%`, x: 0 };
		case "start":
			return { x: `${-percent}%`, y: 0 };
		case "end":
			return { x: `${percent}%`, y: 0 };
		default:
			return { x: 0, y: 0 };
	}
}

function buildSlotVariants(
	xHidden: string | number,
	yHidden: string | number,
	isHorizontal: boolean,
): Variants {
	return {
		hidden: {
			x: xHidden,
			y: yHidden,
			opacity: 0,
			scale: 0.9,
			width: isHorizontal ? 0 : "auto",
			height: isHorizontal ? "auto" : 0,
			transition: SPRING_TRANSITION,
		},
		visible: {
			x: 0,
			y: 0,
			opacity: 1,
			scale: 1,
			width: "auto",
			height: "auto",
			transition: SPRING_TRANSITION,
		},
	};
}

function applyItemClassName(
	nodes: React.ReactNode,
	itemClassName: string,
): React.ReactNode {
	return React.Children.map(nodes, (child) => {
		if (!React.isValidElement(child)) return child;
		const element = child as React.ReactElement<{ className?: string }>;
		return React.cloneElement(element, {
			className: cn(element.props.className, itemClassName),
		});
	});
}

// --- Component ---

/**
 * Internal component for rendering the toolbar logic.
 * Consumers should use HorizontalFloatingToolbar or VerticalFloatingToolbar.
 */
const FloatingToolbarBase = React.forwardRef<
	HTMLDivElement,
	FloatingToolbarProps
>(
	(
		{
			expanded,
			orientation = "horizontal",
			variant = "standard",
			colors: customColors,
			shape = "full",
			contentPadding,
			scrollBehavior,
			startContent,
			endContent,
			children,
			disableScrollTranslation,
			disableLayoutAnimation,
			itemGap = FloatingToolbarTokens.ContainerBetweenSpace,
			childrenAlignment = "center",
			itemClassName,
			className,
			style,
			"aria-label": ariaLabel,
			...props
		},
		ref,
	) => {
		const colors = getToolbarColors(variant, customColors);
		const isHorizontal = orientation === "horizontal";
		const isStringPadding = typeof contentPadding === "string";

		const cssVars = {
			"--toolbar-bg": colors.toolbarContainerColor,
			"--toolbar-color": colors.toolbarContentColor,
			"--toolbar-size": `${FloatingToolbarTokens.ContainerHeight}px`,
			...(isStringPadding ? {} : contentPadding),
			...style,
		} as React.CSSProperties;

		const isScrolledOff = scrollBehavior && scrollBehavior.offset < 0;
		const isXr = variant === "xr";
		const shadowClass = isXr
			? "shadow-lg border border-white/10 backdrop-blur-md"
			: expanded
				? "shadow-md"
				: "shadow-sm";
		const containerShapeClass =
			shape === "full" ? "rounded-full" : "rounded-2xl";

		const leadingVariants = buildSlotVariants(
			isHorizontal ? "-100%" : 0,
			isHorizontal ? 0 : "100%",
			isHorizontal,
		);
		const trailingVariants = buildSlotVariants(
			isHorizontal ? "100%" : 0,
			isHorizontal ? 0 : "-100%",
			isHorizontal,
		);

		const processNodes = (nodes: React.ReactNode) =>
			itemClassName ? applyItemClassName(nodes, itemClassName) : nodes;

		const scrollTranslation = resolveScrollTranslation(
			scrollBehavior,
			disableScrollTranslation,
		);

		return (
			<ToolbarContext.Provider value={{ orientation, itemClassName }}>
				<m.div
					ref={ref}
					initial={false}
					animate={scrollTranslation}
					transition={SPRING_TRANSITION}
				>
					<m.div
						role="toolbar"
						aria-label={ariaLabel || "Floating Toolbar"}
						aria-orientation={orientation}
						aria-expanded={expanded}
						inert={isScrolledOff ? true : undefined}
						{...(disableLayoutAnimation ? {} : { layout: "position" as const })}
						transition={SPRING_TRANSITION}
						style={{
							...cssVars,
							backgroundColor: "var(--toolbar-bg)",
							color: "var(--toolbar-color)",
						}}
						className={cn(
							"flex pointer-events-auto relative text-(--toolbar-color) [&_.text-m3-on-surface-variant]:text-current",
							isHorizontal
								? "max-w-[min(90vw,100%)] h-(--toolbar-size)"
								: "max-h-[min(90vh,100%)] w-(--toolbar-size) h-fit",
							containerShapeClass,
							shadowClass,
							className,
						)}
						{...props}
					>
						<div
							className={cn(
								"flex-1 w-full h-full rounded-[inherit] scrollbar-hide",
								isHorizontal
									? "overflow-x-auto overflow-y-visible"
									: "overflow-y-auto overflow-x-visible",
							)}
						>
							<div
								className={cn(
									"flex items-center min-w-0 min-h-0",
									isHorizontal ? "flex-row h-full" : "flex-col w-full",
									isStringPadding
										? contentPadding
										: isHorizontal
											? "px-2"
											: "py-2",
								)}
								style={{
									gap: itemGap,
									...(isStringPadding ? {} : contentPadding),
								}}
							>
								<AnimatePresence initial={false}>
									{expanded && startContent && (
										<m.div
											key="start"
											variants={leadingVariants}
											initial="hidden"
											animate="visible"
											exit="hidden"
											className={cn(
												"flex shrink-0 items-center overflow-hidden",
												isHorizontal ? "flex-row" : "flex-col",
											)}
											style={{ gap: itemGap }}
										>
											{processNodes(startContent)}
										</m.div>
									)}
								</AnimatePresence>

								<div
									className={cn(
										"flex flex-1 items-center min-w-0 min-h-0 h-full",
										isHorizontal ? "flex-row" : "flex-col",
										ALIGNMENT_CLASS[childrenAlignment],
									)}
									style={{ gap: itemGap }}
								>
									{processNodes(children)}
								</div>

								<AnimatePresence initial={false}>
									{expanded && endContent && (
										<m.div
											key="end"
											variants={trailingVariants}
											initial="hidden"
											animate="visible"
											exit="hidden"
											className={cn(
												"flex shrink-0 items-center overflow-hidden",
												isHorizontal ? "flex-row" : "flex-col",
											)}
											style={{ gap: itemGap }}
										>
											{processNodes(endContent)}
										</m.div>
									)}
								</AnimatePresence>
							</div>
						</div>
					</m.div>
				</m.div>
			</ToolbarContext.Provider>
		);
	},
);
FloatingToolbarBase.displayName = "FloatingToolbarBase";

/**
 * A horizontal floating toolbar that displays navigation and key actions.
 * It can be positioned anywhere on the screen and floats over the rest of the content.
 *
 * @example
 * ```tsx
 * const [expanded, setExpanded] = useState(true);
 * const scrollBehavior = useFloatingToolbarScrollBehavior({ exitDirection: 'bottom' });
 *
 * <HorizontalFloatingToolbar
 *   expanded={expanded}
 *   scrollBehavior={scrollBehavior}
 *   startContent={<IconButton icon="undo" />}
 *   endContent={<IconButton icon="redo" />}
 * >
 *   <IconButton icon="bold" />
 *   <IconButton icon="italic" />
 * </HorizontalFloatingToolbar>
 * ```
 *
 * @see https://m3.material.io/components/toolbars/guidelines
 */
export const HorizontalFloatingToolbar = React.forwardRef<
	HTMLDivElement,
	Omit<FloatingToolbarProps, "orientation">
>((props, ref) => (
	<FloatingToolbarBase ref={ref} orientation="horizontal" {...props} />
));
HorizontalFloatingToolbar.displayName = "HorizontalFloatingToolbar";

/**
 * A vertical floating toolbar that displays navigation and key actions.
 * It floats over the rest of the content.
 *
 * @example
 * ```tsx
 * <VerticalFloatingToolbar expanded={true}>
 *   <IconButton icon="bold" />
 * </VerticalFloatingToolbar>
 * ```
 */
export const VerticalFloatingToolbar = React.forwardRef<
	HTMLDivElement,
	Omit<FloatingToolbarProps, "orientation">
>((props, ref) => (
	<FloatingToolbarBase ref={ref} orientation="vertical" {...props} />
));
VerticalFloatingToolbar.displayName = "VerticalFloatingToolbar";
